import React, { useCallback, useEffect, useMemo, useState } from "react";
import { PageHeader } from "../components/layout/Shell";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Input } from "../components/ui/input";
import { ARGOVBadge } from "../components/shared/ARGOVBadge";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import { useInstitution } from "../context/InstitutionContext";
import { useLang } from "../context/LanguageContext";
import { useModuleName } from "../context/TenantConfigContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Sparkles, ShieldCheck, Settings2, Zap,
  KeyRound, CheckCircle2, XCircle, Loader2,
  Eye, EyeOff, Trash2, RefreshCw, AlertTriangle,
  Globe,
} from "lucide-react";

/**
 * Provider / model catalog. Kept in sync with what the backend
 * `ai_service.resolve_model` accepts. Newer models first.
 *
 * Note: only models that are *actually available* via the Emergent LLM
 * key or institutional credentials should be listed here — picking a
 * non-existent model from the dropdown would fail at first invocation.
 */
const PROVIDERS = [
  {
    value: "anthropic", label: "Anthropic",
    models: ["claude-sonnet-4.5", "claude-haiku-4.5", "claude-opus-4.5"],
  },
  {
    value: "openai", label: "OpenAI",
    models: ["gpt-5.2", "gpt-5-mini", "gpt-4o", "gpt-4o-mini"],
  },
  {
    value: "gemini", label: "Google",
    models: ["gemini-3-pro", "gemini-3-flash", "gemini-2.5-pro", "gemini-nano-banana"],
  },
  {
    value: "sarvam",
    label: "Sarvam AI (India)",
    models: ["sarvam-105b"],
    note: "Requires institution-specific Sarvam API key configured in Provider Settings",
  },
];

const STATUS_STYLES = {
  active: "border-emerald-400/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  coming_soon: "border-amber-400/40 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  disabled: "border-border bg-muted text-muted-foreground",
};

// Canonical Claros module IDs we display section headers for. Order
// matches the sidebar grouping in /app/frontend/src/components/layout/Sidebar.jsx
const MODULE_ORDER = [
  "claros-insights", "claros-ai", "claros-enroll", "claros-core",
  "claros-learn", "claros-launch", "claros-research", "claros-people",
  "claros-alumni", "claros-safe", "claros-green", "claros-comply",
];

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem("academiaos_token");

// ── Sarvam Provider Panel ────────────────────────────────────────────────────

function SarvamProviderPanel({ open, onClose, institutionId }) {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [dailyBudget, setDailyBudget] = useState(100000);
  const [monthlyBudget, setMonthlyBudget] = useState(2000000);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [revoking, setRevoking] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  const sarvam = providers.find((p) => p.provider === "sarvam");

  const load = useCallback(async () => {
    if (!institutionId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/ai/provider-credentials/${institutionId}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (res.ok) {
        const data = await res.json();
        setProviders(data.providers || []);
      }
    } catch (e) {
      /* ignore — will show NOT_CONFIGURED */
    } finally {
      setLoading(false);
    }
  }, [institutionId]);

  useEffect(() => {
    if (open) {
      load();
      setNewKey("");
      setTestResult(null);
      setConfirmRevoke(false);
    }
  }, [open, load]);

  const handleSave = async () => {
    if (!newKey.trim()) return;
    setSaving(true);
    setTestResult(null);
    try {
      const res = await fetch(
        `${API}/api/ai/provider-credentials/${institutionId}/sarvam`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify({
            api_key: newKey.trim(),
            daily_token_budget: dailyBudget,
            monthly_token_budget: monthlyBudget,
          }),
        }
      );
      if (res.ok) {
        toast.success("Sarvam credential saved", {
          description: "Key encrypted at rest. Use Test Connection to verify.",
        });
        setNewKey("");
        await load();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error("Failed to save credential", {
          description: err.detail || "Check server logs.",
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await fetch(
        `${API}/api/ai/provider-credentials/${institutionId}/sarvam/test`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${getToken()}` },
        }
      );
      const data = await res.json();
      setTestResult(data);
    } catch (e) {
      setTestResult({ ok: false, status: "NETWORK_ERROR", error: String(e) });
    } finally {
      setTesting(false);
    }
  };

  const handleRevoke = async () => {
    setRevoking(true);
    try {
      const res = await fetch(
        `${API}/api/ai/provider-credentials/${institutionId}/sarvam`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${getToken()}` },
        }
      );
      if (res.ok) {
        toast.success("Sarvam credential revoked", {
          description: "Encrypted key has been permanently purged.",
        });
        setConfirmRevoke(false);
        setTestResult(null);
        await load();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error("Revocation failed", { description: err.detail });
      }
    } finally {
      setRevoking(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="sarvam-provider-panel">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe className="h-4 w-4 text-primary" />
            AI Provider Settings
            <Badge variant="outline" className="text-[10px] font-mono">Sarvam AI (India)</Badge>
          </DialogTitle>
          <DialogDescription className="text-xs">
            Institution-scoped credentials. Keys are encrypted at rest. Never returned by any API.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* ── Current status ─────────────────────────────────────── */}
          <div
            className={`flex items-start gap-3 rounded-lg border p-3 ${
              sarvam?.configured
                ? "border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/30"
                : "border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30"
            }`}
            data-testid="sarvam-credential-status"
          >
            {sarvam?.configured ? (
              <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400 mt-0.5 shrink-0" />
            ) : (
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">
                {sarvam?.configured ? "Credential Configured" : "NOT_CONFIGURED"}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {sarvam?.configured ? (
                  <>
                    Key suffix: <span className="font-mono">••••{sarvam.key_suffix}</span>
                    {" · "}Daily budget:{" "}
                    {(sarvam.daily_token_budget || 0).toLocaleString()} tokens
                    {" · "}Updated{" "}
                    {sarvam.updated_at ? new Date(sarvam.updated_at).toLocaleDateString() : "—"}
                  </>
                ) : (
                  "No active Sarvam API key. Enter a key below to enable Sarvam Chat and Translation."
                )}
              </div>
            </div>
            {sarvam?.configured && (
              <div className="flex gap-1.5 shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleTest}
                  disabled={testing}
                  data-testid="sarvam-test-btn"
                  className="text-xs h-7"
                >
                  {testing ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3 w-3" />
                  )}
                  <span className="ml-1">Test</span>
                </Button>
                {!confirmRevoke ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setConfirmRevoke(true)}
                    className="text-xs h-7 text-red-600 border-red-200 hover:bg-red-50"
                    data-testid="sarvam-revoke-btn"
                  >
                    <Trash2 className="h-3 w-3" />
                    <span className="ml-1">Revoke</span>
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={handleRevoke}
                    disabled={revoking}
                    className="text-xs h-7"
                    data-testid="sarvam-revoke-confirm-btn"
                  >
                    {revoking ? <Loader2 className="h-3 w-3 animate-spin" /> : "Confirm Revoke"}
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* ── Test result ────────────────────────────────────────── */}
          {testResult && (
            <div
              className={`flex items-start gap-2 rounded-md border p-2.5 text-xs ${
                testResult.ok
                  ? "border-emerald-200 bg-emerald-50 dark:bg-emerald-950/20"
                  : "border-red-200 bg-red-50 dark:bg-red-950/20"
              }`}
              data-testid="sarvam-test-result"
            >
              {testResult.ok ? (
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0 mt-0.5" />
              ) : (
                <XCircle className="h-3.5 w-3.5 text-red-500 shrink-0 mt-0.5" />
              )}
              <div>
                <span className="font-mono font-semibold">{testResult.status}</span>
                {testResult.ok && testResult.response_preview && (
                  <div className="mt-0.5 text-muted-foreground">
                    Model: {testResult.model} · Response: "{testResult.response_preview}"
                  </div>
                )}
                {!testResult.ok && testResult.error && (
                  <div className="mt-0.5 text-red-600">{testResult.error}</div>
                )}
                {!testResult.ok && testResult.message && (
                  <div className="mt-0.5 text-muted-foreground">{testResult.message}</div>
                )}
              </div>
            </div>
          )}

          {/* ── Enter / replace key ────────────────────────────────── */}
          <div className="space-y-3">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {sarvam?.configured ? "Replace API Key" : "Configure API Key"}
            </div>

            <div className="relative">
              <Input
                type={showKey ? "text" : "password"}
                placeholder="api-subscription-key from Sarvam AI console"
                value={newKey}
                onChange={(e) => setNewKey(e.target.value)}
                className="pr-10 font-mono text-sm"
                data-testid="sarvam-key-input"
                autoComplete="off"
              />
              <button
                type="button"
                onClick={() => setShowKey((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                tabIndex={-1}
              >
                {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Daily token budget</label>
                <Input
                  type="number"
                  value={dailyBudget}
                  onChange={(e) => setDailyBudget(Number(e.target.value))}
                  className="text-sm h-8"
                  data-testid="sarvam-daily-budget"
                  min={1000}
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Monthly token budget</label>
                <Input
                  type="number"
                  value={monthlyBudget}
                  onChange={(e) => setMonthlyBudget(Number(e.target.value))}
                  className="text-sm h-8"
                  data-testid="sarvam-monthly-budget"
                  min={10000}
                />
              </div>
            </div>

            <div className="rounded-md bg-muted/50 border border-border px-3 py-2 text-[11px] text-muted-foreground">
              <ShieldCheck className="h-3 w-3 inline mr-1 text-primary" />
              Key encrypted at rest (AES-128-CBC + HMAC-SHA256). Never returned by any API.
              Only the last 4 characters are stored as a display suffix (INV-CRED-02).
            </div>
          </div>

          {/* ── Translation language info ──────────────────────────── */}
          <div className="rounded-lg border border-border bg-card p-3">
            <div className="text-xs font-semibold mb-2">Available capabilities when configured</div>
            <div className="grid grid-cols-1 gap-1.5 text-xs text-muted-foreground">
              <div className="flex items-center gap-2">
                <span className="font-mono bg-muted px-1.5 py-0.5 rounded text-[10px]">sarvam-105b</span>
                <span>AI Chat · 128K context · Indian language support</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-mono bg-muted px-1.5 py-0.5 rounded text-[10px]">sarvam-translate:v1</span>
                <span>Text translation: Telugu ↔ Hindi ↔ English (2000 chars/req)</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-mono bg-muted px-1.5 py-0.5 rounded text-[10px]">saaras:v4</span>
                <span>Speech-to-Text (STT) — reuses existing Saaras key</span>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="sarvam-panel-close">
            Close
          </Button>
          <Button
            onClick={handleSave}
            disabled={!newKey.trim() || saving}
            data-testid="sarvam-save-btn"
          >
            {saving && <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />}
            {sarvam?.configured ? "Replace Credential" : "Save Credential"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ModuleHeader({ canonicalId, count }) {
  const resolved = useModuleName(canonicalId);
  return (
    <div className="flex items-baseline justify-between mt-8 mb-3 first:mt-0" data-testid={`uc-group-${canonicalId}`}>
      <h2 className="text-lg font-semibold tracking-tight">{resolved}</h2>
      <span className="text-xs text-muted-foreground tabular-nums">
        {count} use case{count === 1 ? "" : "s"}
      </span>
    </div>
  );
}

export default function AIUseCases() {
  const { current } = useInstitution();
  const { lang } = useLang();
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [showProviderPanel, setShowProviderPanel] = useState(false);

  const load = useCallback(async () => {
    if (!current) return;
    const r = await api.get(`/ai/use-cases/${current.id}`);
    setItems(r.data);
  }, [current]);

  useEffect(() => {
    load();
  }, [load]);

  const save = useCallback(async () => {
    if (!edit) return;
    try {
      await api.patch(`/ai/use-cases/${current.id}/${edit.key}`, {
        provider: edit.provider,
        model: edit.model,
        status: edit.status,
        human_in_the_loop: edit.human_in_the_loop,
        citations_required: edit.citations_required,
      });
      toast.success("Use case updated", { description: "Change captured in audit log" });
      setEdit(null);
      load();
    } catch (e) {
      toast.error("Could not save");
    }
  }, [edit, current, load]);

  // Group items by canonical Claros module. Items without a mapping fall
  // into an "Other" bucket so legacy rows still appear during migration.
  const groups = useMemo(() => {
    const byModule = {};
    for (const uc of items) {
      const key = uc.canonical_module || "_other";
      (byModule[key] = byModule[key] || []).push(uc);
    }
    return [
      ...MODULE_ORDER.filter((m) => byModule[m]).map((m) => [m, byModule[m]]),
      ...(byModule._other ? [["_other", byModule._other]] : []),
    ];
  }, [items]);

  if (!current) return null;

  return (
    <div data-testid="ai-use-cases-page">
      <PageHeader
        eyebrow="AI Layer · Catalog"
        title="AI Use Cases"
        description="AI capabilities grouped by canonical module. Provider, model and governance policy live in the database — never in code."
        actions={
          <>
            <ARGOVBadge type="EVIDENCE_AVAILABLE" />
            <Badge variant="outline" className="gap-1.5">
              <ShieldCheck className="h-3 w-3" />
              AI TRiSM · Policy active
            </Badge>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowProviderPanel(true)}
              data-testid="open-provider-settings-btn"
              className="gap-1.5"
            >
              <KeyRound className="h-3.5 w-3.5" />
              Provider Settings
            </Button>
          </>
        }
      />

      <div className="p-6 lg:p-8">
        {groups.map(([mod, ucs]) => (
          <React.Fragment key={mod}>
            <ModuleHeader canonicalId={mod === "_other" ? "" : mod} count={ucs.length} />
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {ucs.map((uc) => (
                <UseCaseCard
                  key={uc.key}
                  uc={uc}
                  lang={lang}
                  onEdit={() => setEdit({ ...uc })}
                />
              ))}
            </div>
          </React.Fragment>
        ))}
      </div>

      {/* ── Provider Settings dialog (Sarvam credential management) ──── */}
      <SarvamProviderPanel
        open={showProviderPanel}
        onClose={() => setShowProviderPanel(false)}
        institutionId={current?.id}
      />

      {/* ── Use case configure dialog ─────────────────────────────────── */}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              {edit?.name_en}
              <Badge variant="outline" className="text-[10px] font-mono">{edit?.code}</Badge>
            </DialogTitle>
            <DialogDescription className="text-xs">
              Configure provider, model and governance policy for this AI use case.
            </DialogDescription>
          </DialogHeader>
          {edit && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium">Provider</label>
                  <Select
                    value={edit.provider}
                    onValueChange={(v) =>
                      setEdit((e) => ({
                        ...e,
                        provider: v,
                        model: PROVIDERS.find((p) => p.value === v)?.models[0] || e.model,
                      }))
                    }
                  >
                    <SelectTrigger data-testid="uc-provider"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PROVIDERS.map((p) => (
                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium">Model</label>
                  <Select value={edit.model} onValueChange={(v) => setEdit((e) => ({ ...e, model: v }))}>
                    <SelectTrigger data-testid="uc-model"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(PROVIDERS.find((p) => p.value === edit.provider)?.models || []).map((m) => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {edit.provider === "sarvam" && (
                <div className="flex items-start gap-2 rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/20 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  <span>
                    Sarvam requires an institution-specific API key.{" "}
                    <button
                      className="underline font-medium"
                      onClick={() => { setEdit(null); setShowProviderPanel(true); }}
                    >
                      Configure in Provider Settings
                    </button>{" "}
                    before activating this use case.
                  </span>
                </div>
              )}
              <div className="space-y-1.5">
                <label className="text-xs font-medium">Status</label>
                <Select value={edit.status} onValueChange={(v) => setEdit((e) => ({ ...e, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="coming_soon">Coming soon</SelectItem>
                    <SelectItem value="disabled">Disabled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between border-t border-border pt-3">
                <div>
                  <div className="text-sm font-medium">Human-in-the-loop</div>
                  <div className="text-[11px] text-muted-foreground">Approval required on irreversible actions</div>
                </div>
                <Switch
                  checked={!!edit.human_in_the_loop}
                  onCheckedChange={(v) => setEdit((e) => ({ ...e, human_in_the_loop: v }))}
                />
              </div>
              <div className="flex items-center justify-between border-t border-border pt-3">
                <div>
                  <div className="text-sm font-medium">Citations required</div>
                  <div className="text-[11px] text-muted-foreground">Force RAG grounding on all responses</div>
                </div>
                <Switch
                  checked={!!edit.citations_required}
                  onCheckedChange={(v) => setEdit((e) => ({ ...e, citations_required: v }))}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)}>Cancel</Button>
            <Button onClick={save} data-testid="uc-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function UseCaseCard({ uc, lang, onEdit }) {
  // Only show Arabic name as a secondary label when the active interface
  // language is Arabic. Other locales (en/hi/te for VCE/ISB) just see the
  // English name — no irrelevant secondary line clutter.
  const isArabicUI = lang === "ar";
  const primary = isArabicUI ? uc.name_ar : uc.name_en;
  const secondary = isArabicUI ? uc.name_en : null;
  return (
    <div
      data-testid={`use-case-${uc.key}`}
      className="rounded-lg border border-border bg-card p-5 transition hover:-translate-y-0.5 hover:shadow-md"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary text-xl font-semibold"
            aria-hidden
          >
            {uc.glyph}
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-mono">
              {uc.code}
            </div>
            <div className="text-sm font-semibold leading-tight truncate" title={primary}>
              {primary}
            </div>
            {secondary && (
              <div className="text-[11px] text-muted-foreground truncate" title={secondary}>
                {secondary}
              </div>
            )}
          </div>
        </div>
        <Badge variant="outline" className={`text-[10px] ${STATUS_STYLES[uc.status] || ""}`}>
          {uc.status.replace("_", " ")}
        </Badge>
      </div>

      <p className="mt-4 text-xs text-muted-foreground leading-relaxed line-clamp-3">
        {uc.description}
      </p>

      <div className="mt-4 flex items-center justify-between rounded-md border border-border bg-muted/30 px-3 py-2">
        <div>
          <div className="label-eyebrow">{uc.metric}</div>
          <div className="text-base font-semibold tabular-nums">{uc.latency}</div>
        </div>
        <Zap className="h-4 w-4 text-accent" />
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2 text-[11px]">
        <div className="rounded-md border border-border px-2 py-1.5">
          <div className="text-muted-foreground">Provider</div>
          <div className="font-mono">{uc.provider}</div>
        </div>
        <div className="rounded-md border border-border px-2 py-1.5">
          <div className="text-muted-foreground">Model</div>
          <div className="font-mono truncate">{uc.model}</div>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between border-t border-border pt-3">
        <div className="flex gap-1.5">
          {uc.human_in_the_loop && (
            <Badge variant="secondary" className="text-[10px]">HITL</Badge>
          )}
          {uc.citations_required && (
            <Badge variant="secondary" className="text-[10px]">Citations</Badge>
          )}
        </div>
        <Button size="sm" variant="ghost" onClick={onEdit} data-testid={`configure-${uc.key}`}>
          <Settings2 className="h-3.5 w-3.5 me-1" /> Configure
        </Button>
      </div>
    </div>
  );
}
